/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  TextField,
  Backdrop,
  CircularProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Topheader from "../../../../Layout/header/header";
import Sidebar from "../sidebar/sidebar";
import VehicleDetailsDialog from "./VehicleDetailsDialog";
import DeleteVehicleListDialog from "./DeleteVehicleListDialog";
import templateFile from "../../../../assets/files/upload.csv";
import ApiManager from "../../../../Services/ApiManager/ApiManager";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "bold",
    borderLeft: "none",
    borderRight: "none",
    padding: "8px 16px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderLeft: "none",
    borderRight: "none",
    padding: "8px 16px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
}));

// Custom Delete Icon SVG Component
const CustomDeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.55534 16C3.06645 16 2.64793 15.8259 2.29978 15.4778C1.95163 15.1296 1.77756 14.7111 1.77756 14.2222V2.66667H0.888672V0.888889H5.33312V0H10.6664V0.888889H15.1109V2.66667H14.222V14.2222C14.222 14.7111 14.0479 15.1296 13.6998 15.4778C13.3516 15.8259 12.9331 16 12.4442 16H3.55534ZM12.4442 2.66667H3.55534V14.2222H12.4442V2.66667ZM5.33312 12.4444H7.11089V4.44444H5.33312V12.4444ZM8.88867 12.4444H10.6664V4.44444H8.88867V12.4444Z" fill="#121415" fillOpacity="0.6"/>
  </svg>
);

export default function VehicleListSettings({ portfolioName }) {
  const [loader, setLoader] = useState(false);
  const [dataFromChild, setDataFromChild] = useState("");

  const [listingName, setListingName] = useState("");
  const [listingNameError, setListingNameError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVehicleList, setSelectedVehicleList] = useState(null);
  const [fileError, setFileError] = useState("");
  const [apiError, setApiError] = useState("");
  const [csvError, setCsvError] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [vehicleLists, setVehicleLists] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [forceUninstallRequired, setForceUninstallRequired] = useState(false);

  function handleDataFromChild(data) {
    // Only update if the data has actually changed
    if (data !== dataFromChild) {
      setDataFromChild(data);
    }
  }

  // Get current portfolio - use dataFromChild if available, otherwise use portfolioName prop
  const currentPortfolio = dataFromChild || portfolioName;



  const handleDelete = (vehicle) => {
    // Open delete confirmation dialog
    setVehicleToDelete(vehicle);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoader(true);
      
      // Call API to delete vehicle list
      await ApiManager.deleteVehicleList(currentPortfolio, vehicleToDelete.id);
      
      // Show success toast message
      setSnackbarMessage("Vehicle list deleted successfully");
      setOpenSnackbar(true);
      
      // Close the dialog and clear state
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
      
      // Refresh the table data
      await fetchVehicleLists(currentPortfolio);
      
    } catch {
      // Show error toast message
      setSnackbarMessage("Failed to delete vehicle list. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setLoader(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    // Reset all form fields
    setListingName("");
    setListingNameError("");
    setSelectedFileName("");
    setSelectedFile(null);
    setFileError("");
    setApiError("");
    setCsvError("");
    setStartDate("");
    setEndDate("");
    setDateError("");
    setEndDateError("");
    setForceUninstallRequired(false);
    // Clear the file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const getTodayUtcDateString = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, "0");
    const day = String(now.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const toUtcStartDateTime = (dateStr) => {
    const dateStrs = dateStr ? new Date(`${dateStr}T00:00:00Z`) : null;
    return dateStrs ? dateStrs.getTime() : 0;
  }

  const toUtcEndDateTime = (dateStr) => {
    const dateStrs = dateStr ? new Date(`${dateStr}T23:59:59Z`) : null;
    return dateStrs ? dateStrs.getTime() : 0;
  }

  const validateDates = (nextStartDate, nextEndDate) => {
    const todayUtc = getTodayUtcDateString();

    if (nextStartDate && nextStartDate < todayUtc) {
      return "Start date cannot be earlier than today";
    }

    if (nextStartDate && nextEndDate && nextEndDate < nextStartDate) {
      return "End date cannot be earlier than start date";
    }

    return "";
  };

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    const errorMessage = validateDates(value, endDate);

    setStartDate(value);
    setDateError(errorMessage);

    if (endDate && value && endDate < value) {
      setEndDate("");
    }
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    const errorMessage = validateDates(startDate, value);

    setEndDate(value);
    setDateError(errorMessage);
    
    // Clear end date error if end date is provided
    if (value) {
      setEndDateError("");
    }
  };

  const handleForceUninstallChange = (e) => {
    const isChecked = e.target.checked;
    setForceUninstallRequired(isChecked);
    
    // If force uninstall is checked and no end date, show error
    if (isChecked && !endDate) {
      setEndDateError("End date is required when Force Uninstall Upon Expiry is enabled");
    } else {
      setEndDateError("");
    }
  };

  // Format date to "dd Mon yyyy" format (e.g., "02 Mar 2026")
  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return "";
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    // Handle "yyyy-mm-dd" string format from date inputs (local input values)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}`;
    }
    
    // For timestamps/Date objects, display in UTC format
    const date = new Date(dateValue);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    
    return `${day} ${month} ${year}`;
  };

  const getActiveStatus = (vehicleStartDate, vehicleEndDate) => {
    // If no end date, it's always active
    if (!vehicleEndDate) {
      return "Yes";
    }

    // Compare dates properly - handle both timestamp and date string formats
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = today.getTime();
    
    // Convert endDate to timestamp if needed
    const endDateTimestamp = typeof vehicleEndDate === 'number' 
      ? vehicleEndDate 
      : new Date(vehicleEndDate).getTime();
    
    // If today's date is greater than end date, it's inactive
    return todayTimestamp > endDateTimestamp ? "No" : "Yes";
  };

  const handleViewVehicleDetails = (vehicle) => {
    setSelectedVehicleList(vehicle);
    setDialogOpen(true);
  };

  const handleCloseDialog = async () => {
    setDialogOpen(false);
    setSelectedVehicleList(null);
    
    // Refresh the vehicle lists table when dialog closes
    if (currentPortfolio) {
      await fetchVehicleLists(currentPortfolio);
    }
  };

  const handleBrowseFile = (event) => {
    // Handle file upload
    const file = event.target.files[0];
    if (file) {
      // Check if file is csv format only
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop().toLowerCase();
      
      if (fileExtension !== 'csv') {
        setCsvError("Please upload only .csv files");
        setSelectedFileName("");
        setSelectedFile(null);
        // Clear the file input
        event.target.value = '';
        return;
      }
      
      // Clear any previous error and store the file name and file object
      setFileError("");
      setApiError("");
      setCsvError("");
      setSelectedFileName(fileName);
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = () => {
    // Create a link element to download the template file
    const link = document.createElement('a');
    link.href = templateFile;
    link.download = 'upload.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const fetchVehicleLists = async (portfolioName) => {
    try {
      setLoader(true);
      const response = await ApiManager.getVehicleLists(portfolioName);
      
      if (Array.isArray(response?.data)) {
        setVehicleLists(response.data);
      } else if (response && Array.isArray(response)) {
        setVehicleLists(response);
      } else {
        setVehicleLists([]);
      }
    } catch {
      setVehicleLists([]);
    } finally {
      setLoader(false);
    }
  };

  // Fetch vehicle lists when component mounts or when currentPortfolio changes
  useEffect(() => {
    if (currentPortfolio) {
      fetchVehicleLists(currentPortfolio);
    }
  }, [currentPortfolio]);

  const resetFormFields = () => {
    setListingName("");
    setListingNameError("");
    setSelectedFileName("");
    setSelectedFile(null);
    setFileError("");
    setApiError("");
    setCsvError("");
    setStartDate("");
    setEndDate("");
    setDateError("");
    setEndDateError("");
    setForceUninstallRequired(false);
    
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const buildVehicleListData = () => {
    const vehicleListData = {
      name: listingName.trim(),
      groupType: "PORTFOLIO",
      memberType: "VEHICLE",
      startDate: toUtcStartDateTime(startDate),
      forceUninstallRequired: forceUninstallRequired || false
    };
    
    if (endDate) {
      vehicleListData.endDate = toUtcEndDateTime(endDate);
    }
    
    return vehicleListData;
  };

  const handleFileUpload = async (vehicleListId) => {
    await ApiManager.uploadVehicleListFile(currentPortfolio, vehicleListId, selectedFile);
  };

  const handleSave = async () => {
    let createdVehicleListId = null;
    
    try {
      setLoader(true);
      setApiError("");
      
      const vehicleListData = buildVehicleListData();
      
      const createResponse = await ApiManager.createVehicleList(currentPortfolio, vehicleListData);
      
      if (!createResponse || (!createResponse.data?.id && !createResponse.id)) {
        setFileError("Failed to create vehicle list. Please try again.");
        return;
      }
      
      createdVehicleListId = createResponse.data?.id || createResponse.id;
      
      try {
        await handleFileUpload(createdVehicleListId);
        await fetchVehicleLists(currentPortfolio);
        
        setSnackbarMessage(`${listingName.trim()} has been successfully created`);
        setOpenSnackbar(true);
        
        resetFormFields();
        setCreateDialogOpen(false);
      } catch (uploadError) {
        
        try {
          await ApiManager.deleteVehicleList(currentPortfolio, createdVehicleListId);
        } catch {
          // rollback error handled
        }
        
        setApiError("Failed to upload file. Vehicle list creation has been cancelled.");
        
        setSelectedFile(null);
        setSelectedFileName("");
        const fileInput = document.getElementById('file-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        
        throw uploadError;
      }
    } catch {
      setApiError("We encountered issues while processing your uploaded file. Please correct the following issues before re-uploading:\n\n• Missing VINs: All entries must include a valid VIN.\n• Invalid or Blank Rows: Remove any incomplete or empty entries.\n• Template Mismatch: Use the official template to ensure proper formatting.");
      
      setSelectedFile(null);
      setSelectedFileName("");
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    } finally {
      setLoader(false);
    }
  };

  // Validation functions for Listing Name
  const validateListingName = (name) => {
    // Skip validation for empty string (required validation is handled separately)
    if (!name.trim()) {
      return "";
    }

    // Check length (max 70 characters)
    if (name.length > 70) {
      return "Listing name cannot exceed 70 characters";
    }

    // Check if spaces are present - spaces are not allowed
    if (/\s/.test(name)) {
      return "Spaces are not allowed in listing name";
    }

    // Check for allowed characters: alphanumeric, dot, comma, underscore, hyphen (no spaces)
    const allowedPattern = /^[a-zA-Z0-9.,_-]+$/;
    if (!allowedPattern.test(name)) {
      return "Only alphanumeric characters, dot, comma, underscore, and hyphen are allowed";
    }

    return "";
  };

  const checkDuplicateListingName = (name) => {
    if (!name.trim()) return false;
    
    const trimmedName = name.trim();
    const isDuplicate = vehicleLists.some(vehicle => 
      vehicle.name && vehicle.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    return isDuplicate;
  };

  const handleListingNameChange = (e) => {
    const value = e.target.value;
    setListingName(value);
    
    // Validate the input
    const validationError = validateListingName(value);
    if (validationError) {
      setListingNameError(validationError);
      return;
    }
    
    // Check for duplicates
    if (checkDuplicateListingName(value)) {
      setListingNameError("This listing name already exists");
      return;
    }
    
    // Clear error if validation passes
    setListingNameError("");
  };

  // Check if save button should be disabled (don't disable for API errors)
  const isSaveDisabled =
    !selectedFile ||
    !listingName.trim() ||
    !startDate ||
    fileError ||
    listingNameError ||
    csvError ||
    !!dateError ||
    (forceUninstallRequired && !endDate);

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Box
        sx={{
          marginTop: "3%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "-webkit-box",
          }}
        >
          <Sidebar sendDataToParent={handleDataFromChild} page="closed-beta" />
          <Box sx={{ flexGrow: 1, marginTop: "16px", marginLeft: "20px" }}>
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              {/* Header with back button */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, ml: 0, mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
                    {currentPortfolio} - Closed Beta
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent:"flex-end", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  sx={{ 
                    backgroundColor: "#424242", 
                    color: "white",
                    whiteSpace: "nowrap",
                    minWidth: "180px",
                    flexShrink: 0,
                    fontWeight: 500,
                    fontSize: "14px",
                    textTransform: "none"
                  }}
                >
                  Download Template
                </Button>
                 <Button
                  variant="contained"
                  onClick={handleOpenCreateDialog}
                  sx={{ 
                    backgroundColor: "#0476E2", 
                    color: "white",
                    whiteSpace: "nowrap",
                    minWidth: "180px",
                    flexShrink: 0,
                    fontWeight: 500,
                    fontSize: "14px",
                    textTransform: "none"
                  }}
                >
                  Create Vehicle list
                </Button>
                </Box>
              </Box>

              {/* Tab Content */}
              <Box sx={{ width: "100%" }}>
                  {/* Vehicles List Table */}
                  <Typography sx={{ fontSize: "16px", fontWeight: 600, mb: 2 }}>
                    Vehicles List
                  </Typography>

                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} aria-label="vehicle lists table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell sx={{ width: "30%" }}>Vehicle List</StyledTableCell>
                          <StyledTableCell align="left" sx={{ width: "25%" }}>No. of Vehicles</StyledTableCell>
                          <StyledTableCell align="left" sx={{ width: "25%" }}>Apps Associated</StyledTableCell>
                          <StyledTableCell align="left" sx={{ width: "25%" }}>Beta Duration</StyledTableCell>
                          <StyledTableCell align="left" sx={{ width: "25%" }}>Active</StyledTableCell>
                          <StyledTableCell align="right" sx={{ width: "20%" }}></StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vehicleLists.length === 0 ? (
                          <StyledTableRow>
                            <StyledTableCell colSpan={4} align="center" sx={{ py: 4 }}>
                              <Typography sx={{ color: "#717273", fontStyle: "italic" }}>
                                No vehicle lists available
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                        ) : (
                          vehicleLists.map((vehicle) => (
                            <StyledTableRow key={vehicle.id}>
                              <StyledTableCell 
                                sx={{ 
                                  fontWeight: "normal",
                                  cursor: "pointer",
                                  "&:hover": {
                                    textDecoration: "underline",
                                    fontWeight: "bold"
                                  }
                                }}
                              >
                                {vehicle.name}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {vehicle.vehicleCount}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {vehicle.packageIds && Array.isArray(vehicle.packageIds) ? vehicle.packageIds.length : 0}
                              </StyledTableCell>
                              <StyledTableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                                {(() => {
                                  if (!vehicle.startDate) return "N/A";
                                  if (vehicle.endDate) return `${formatDateDisplay(vehicle.startDate)} - ${formatDateDisplay(vehicle.endDate)}`;
                                  return formatDateDisplay(vehicle.startDate);
                                })()}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {getActiveStatus(vehicle.startDate, vehicle.endDate)}
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, marginRight: -1 }}>
                                  <IconButton
                                    onClick={() => handleDelete(vehicle)}
                                    disabled={sessionStorage.getItem("readonly_OemAdmin") === "true"}
                                    size="small"
                                    sx={{ color: "#717273" }}
                                  >
                                    <CustomDeleteIcon />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    sx={{ color: "#717273" }}
                                    onClick={() => handleViewVehicleDetails(vehicle)}
                                  >
                                    <KeyboardArrowRightIcon />
                                  </IconButton>
                                </Box>
                              </StyledTableCell>
                            </StyledTableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Create Vehicle List Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
            Create Vehicle List
          </Typography>
          <IconButton onClick={handleCloseCreateDialog} size="small" sx={{ color: "#717273" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                border: fileError ? "2px dashed #f44336" : "2px dashed #ccc",
                borderRadius: 1,
                p: 2,
                textAlign: "center",
                minHeight: 70,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f7f7f7",
                mb: 2,
              }}
            >
              <input
                type="file"
                id="file-upload"
                style={{ display: "none", cursor:sessionStorage.getItem("readonly_OemAdmin") === "true" ? "not-allowed":"pointer" }}
                accept=".csv"
                disabled={sessionStorage.getItem("readonly_OemAdmin") === "true"}
                onChange={handleBrowseFile}
              />
              <Typography variant="body2" sx={{ fontSize: "13px" }}>
                <label
                  htmlFor="file-upload"
                  style={{
                    color: "#0476e2",
                    textTransform: "none",
                    cursor: "pointer",
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  Browse
                </label>
                {" Upload the VIN file here"}
              </Typography>
              {selectedFileName && (
                <Typography variant="body2" sx={{ mt: 1, fontSize: "12px", color: "#0476e2", fontWeight: 500 }}>
                  Selected: {selectedFileName}
                </Typography>
              )}
              {csvError && (
                <Typography variant="body2" sx={{ mt: 1, fontSize: "12px", color: "#f44336" }}>
                  {csvError}
                </Typography>
              )}
            </Box>

            <Typography variant="body2" 
            sx={{ 
            mb: 0.5, 
            fontWeight: 600, 
            fontSize: "16px",
            lineHeight:"100%",
            color: "#2A2C2D"
            }}>
              Listing Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={listingName}
              onChange={handleListingNameChange}
              size="small"
              placeholder="Listing Name"
              error={!!listingNameError}
              helperText={listingNameError}
              slotProps={{
                formHelperText: {
                  sx: {
                    fontSize: "12px",
                    color: "#f44336",
                    marginTop: "4px",
                    marginLeft: 0,
                  },
                },
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 1, border: "1px solid #e0e0e0", p: 2, borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx=
                {{ 
                fontSize: "14px", 
                fontWeight: 600, 
                color: "#2A2C2D",
                lineHeight:"100%"
                }}>
                  Start Date <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box 
                  sx={{ position: "relative", minWidth: 120, cursor: "pointer" }}
                  onClick={() => document.getElementById('start-date-input').showPicker()}
                >
                  <input
                    type="date"
                    id="start-date-input"
                    value={startDate}
                    onChange={handleStartDateChange}
                    min={getTodayUtcDateString()}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                  />
                  <Box
                    sx={{
                      height: 28,
                      fontSize: "12px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #ccc",
                      cursor: "pointer",
                      minWidth: 120,
                      pointerEvents: "none",
                      color: "#717273",
                    }}
                  >
                    <span>{startDate ? formatDateDisplay(startDate) : "Select date"}</span>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: "#717273", ml: 1 }} />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ 
                fontSize: "14px", 
                fontWeight: 600, 
                color: "#2A2C2D",
                lineHeight:"100%",
                whiteSpace: "nowrap"
                }}>
                  End Date {forceUninstallRequired ? (
                    <span style={{ color: "red" }}>*</span>
                  ) : (
                    <span style={{ color: "#717273", fontWeight:'400', fontSize:'14px',lineHeight:"100%", fontStyle:"italic" }}>(Optional)</span>
                  )}
                </Typography>
                <Box 
                  sx={{ position: "relative", minWidth: 120, cursor: "pointer" }}
                  onClick={() => document.getElementById('end-date-input').showPicker()}
                >
                  <input
                    type="date"
                    id="end-date-input"
                    value={endDate}
                    onChange={handleEndDateChange}
                    min={startDate || getTodayUtcDateString()}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                  />
                  <Box
                    sx={{
                      height: 28,
                      fontSize: "12px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #ccc",
                      cursor: "pointer",
                      minWidth: 120,
                      pointerEvents: "none",
                      color: "#717273",
                    }}
                  >
                    <span>{endDate ? formatDateDisplay(endDate) : "Select date"}</span>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: "#717273", ml: 1 }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {(dateError || endDateError) && (
              <Typography variant="body2" sx={{ fontSize: "12px", color: "#f44336", mb: 1 }}>
                {dateError || endDateError}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox 
                  size="small" 
                  checked={forceUninstallRequired}
                  onChange={handleForceUninstallChange}
                />
              }
              label={<Typography sx={{ fontSize: "14px", fontWeight:"500", lineHeight:"20px", letterSpacing:'0.25px', color:"#000000" }}>Force Uninstall Upon Expiry</Typography>}
              sx={{ mb: 1 }}
            />

            {(fileError || apiError) && (
              <Box sx={{ mt: 1 }}>
                {fileError && (
                  <Typography variant="body2" sx={{ fontSize: "12px", color: "#f44336", mb: 1 }}>
                    {fileError}
                  </Typography>
                )}
                {apiError && (
                  <Typography variant="body2" sx={{ fontSize: "12px", color: "#f44336", mb: 1, whiteSpace: "pre-line" }}>
                    {apiError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseCreateDialog}
            sx={{
              textTransform: "none",
              borderColor: "#d0d0d0",
              color: "#2A2C2D",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight:'100%',
              padding:"10px 14px 10px 14px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isSaveDisabled || sessionStorage.getItem("readonly_OemAdmin") === "true"}
            sx={{
              backgroundColor: isSaveDisabled ? "#b3d7f2" : "#0476e2",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight:'100%',
              minWidth: "80px",
              "&:hover": {
                backgroundColor: isSaveDisabled ? "#b3d7f2" : "#0365c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#b3d7f2 !important",
                color: "white !important",
                opacity: 1,
              },
              padding:"10px 14px 10px 14px",
              color:"#FFFFFF"
            }}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Details Dialog */}
      <VehicleDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        vehicleListName={selectedVehicleList?.name}
        portfolioId={currentPortfolio} 
        vehicleListId={selectedVehicleList?.id}
        associatedPackages={selectedVehicleList?.packageIds}
        startDateUTCString={selectedVehicleList?.startDate}
        endDateUTCString={selectedVehicleList?.endDate}
        forceUninstallRequired={selectedVehicleList?.forceUninstallRequired}
        onRefresh={() => fetchVehicleLists(currentPortfolio)}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteVehicleListDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        vehicleToDelete={vehicleToDelete}
        portfolioId={currentPortfolio}
      />

      {/* Success/Error Toast */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />
    </>
  );
}

VehicleListSettings.propTypes = {
  portfolioName: PropTypes.string,
};
